import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../contexts/SettingsContext';
import api from '../services/api';
import './Support.css';

const Support = () => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({
    subject: '',
    description: '',
    priority: ''
  });
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: '',
    role: '',
    organizationName: ''
  });
  
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    priority: 'Medium'
  });

  // Get user information on component mount
  useEffect(() => {
    const getCurrentUser = () => {
      try {
        const userStr = sessionStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : null;
        
        if (user) {
          setUserInfo({
            name: user.name || 'Unknown User',
            email: user.email || 'No email provided',
            role: user.role || 'Unknown Role',
            organizationName: settings.society_name || user.organization_name || 'Unknown Organization'
          });
        } else {
          // If no user is logged in, show guest information
          setUserInfo({
            name: 'Guest User',
            email: 'Not logged in',
            role: 'Visitor',
            organizationName: settings.society_name || 'General Inquiry'
          });
        }
      } catch (err) {
        console.error('Error getting user info:', err);
        setUserInfo({
          name: 'Unknown User',
          email: 'Error retrieving email',
          role: 'Unknown',
          organizationName: settings.society_name || 'Unknown Organization'
        });
      }
    };

    getCurrentUser();
  }, [settings.society_name]);

  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'subject':
        if (!value || value.trim().length === 0) {
          error = 'Subject is required';
        } else if (value.trim().length > 200) {
          error = 'Subject must be less than 200 characters';
        }
        break;
      case 'description':
        if (!value || value.trim().length === 0) {
          error = 'Description is required';
        } else if (value.trim().length > 2000) {
          error = 'Description must be less than 2000 characters';
        }
        break;
      case 'priority':
        if (!value) {
          error = 'Priority is required';
        }
        break;
      default:
        break;
    }
    
    return error;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validate the field and update errors
    const error = validateField(name, value);
    setFormErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const validateForm = () => {
    const errors = {
      subject: validateField('subject', formData.subject),
      description: validateField('description', formData.description),
      priority: validateField('priority', formData.priority)
    };

    setFormErrors(errors);
    return !Object.values(errors).some(error => error !== '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare support ticket data
      const supportData = {
        subject: formData.subject,
        description: formData.description,
        priority: formData.priority,
        user_name: userInfo.name,
        user_email: userInfo.email,
        user_role: userInfo.role,
        organization_name: userInfo.organizationName
      };

      // Submit to API
      await api.post('/api/support/submit', supportData);

      // Show success state
      setSubmitted(true);
      
      // Reset form
      setFormData({
        subject: '',
        description: '',
        priority: 'Medium'
      });
      setFormErrors({
        subject: '',
        description: '',
        priority: ''
      });

    } catch (err) {
      console.error('Error submitting support ticket:', err);
      setError(err.response?.data?.message || 'Failed to submit support ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="support-page">
        <div className="support-container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="support-card">
                <div className="card-body text-center">
                  <div className="mb-3">
                    <i className="fas fa-check-circle success-icon"></i>
                  </div>
                  <h2 className="success-title">Support Ticket Submitted!</h2>
                  <p className="success-text">
                    Thank you for contacting us. Your support ticket has been successfully submitted and assigned a reference number.
                  </p>
                  <div className="alert alert-info">
                    <h6 className="alert-heading">What happens next?</h6>
                    <ul className="success-list">
                      <li><i className="fas fa-clock"></i> Our support team will review your request</li>
                      <li><i className="fas fa-envelope"></i> You&apos;ll receive updates via email at <strong>{userInfo.email}</strong></li>
                      <li><i className="fas fa-headset"></i> Expected response time: 24-48 hours</li>
                    </ul>
                  </div>
                  <div className="mt-3">
                    <button 
                      className="btn btn-primary me-2"
                      onClick={() => setSubmitted(false)}
                    >
                      <i className="fas fa-arrow-left me-2"></i>
                      Back to Support Page
                    </button>
                    <button 
                      className="btn btn-success me-2"
                      onClick={() => setSubmitted(false)}
                    >
                      <i className="fas fa-plus me-2"></i>
                      Submit Another Ticket
                    </button>
                    <button 
                      className="btn btn-outline-secondary"
                      onClick={() => window.history.back()}
                    >
                      <i className="fas fa-home me-2"></i>
                      Go to Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="support-page">
      <div className="support-container">
        {/* Header */}
        <div className="support-header">
          <h1 className="support-title">
            <i className="fas fa-headset"></i>
            Support Center
          </h1>
          <p className="support-subtitle">
            Need help? Submit a support ticket and our team will get back to you.
          </p>
          
          {/* Navigation Buttons */}
          <div className="nav-buttons">
            <button 
              className="btn btn-outline-primary btn-sm"
              onClick={() => navigate('/admin/dashboard')}
            >
              <i className="fas fa-tachometer-alt"></i>
              Go to Dashboard
            </button>
            <button 
              className="btn btn-outline-info btn-sm"
              onClick={() => navigate('/admin/support-tickets')}
            >
              <i className="fas fa-ticket-alt"></i>
              View My Tickets
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}

        <div className="row">
          {/* User Information Card */}
          <div className="col-lg-4">
            <div className="support-card h-100">
              <div className="card-header">
                <h6>
                  <i className="fas fa-user"></i>
                  Your Information
                </h6>
              </div>
              <div className="card-body">
                <div className="user-info-item">
                  <div className="user-info-label">Name:</div>
                  <p className="user-info-value">{userInfo.name}</p>
                </div>
                <div className="user-info-item">
                  <div className="user-info-label">Email:</div>
                  <p className="user-info-value">{userInfo.email}</p>
                </div>
                <div className="user-info-item">
                  <div className="user-info-label">Role:</div>
                  <span className="user-role-badge">{userInfo.role}</span>
                </div>
                <div className="user-info-item">
                  <div className="user-info-label">Organization:</div>
                  <p className="user-info-value">{userInfo.organizationName}</p>
                </div>
              </div>
              <div className="card-footer">
                <small>
                  <i className="fas fa-info-circle me-2"></i>
                  This information will be included with your support ticket
                </small>
              </div>
            </div>
          </div>

          {/* Support Form */}
          <div className="col-lg-8">
            <div className="support-card">
              <div className="card-header">
                <h6>
                  <i className="fas fa-ticket-alt"></i>
                  Submit Support Ticket
                </h6>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  {/* Subject and Priority Row */}
                  <div className="form-row mb-3">
                    <div className="col-md-8">
                      <label htmlFor="subject" className="form-label">
                        Subject *
                      </label>
                      <input
                        type="text"
                        className={`form-control ${formErrors.subject ? 'is-invalid' : ''}`}
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        placeholder="Brief description of your issue..."
                        maxLength="200"
                      />
                      {formErrors.subject && (
                        <div className="invalid-feedback">{formErrors.subject}</div>
                      )}
                      <div className="form-text">
                        Provide a clear, concise subject line (max 200 characters)
                      </div>
                    </div>

                    {/* Priority Field */}
                    <div className="col-md-4">
                      <label htmlFor="priority" className="form-label">
                        Priority *
                      </label>
                      <select
                        className={`form-select ${formErrors.priority ? 'is-invalid' : ''}`}
                        id="priority"
                        name="priority"
                        value={formData.priority}
                        onChange={handleInputChange}
                      >
                        <option value="Low">Low - General inquiry</option>
                        <option value="Medium">Medium - Moderate impact</option>
                        <option value="High">High - Critical issue</option>
                      </select>
                      {formErrors.priority && (
                        <div className="invalid-feedback">{formErrors.priority}</div>
                      )}
                    </div>
                  </div>

                  {/* Description Field */}
                  <div className="form-group">
                    <label htmlFor="description" className="form-label">
                      Issue Details *
                    </label>
                    <textarea
                      className={`form-control ${formErrors.description ? 'is-invalid' : ''}`}
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="5"
                      placeholder="Please provide detailed information about your issue:

• What were you trying to do?
• What exactly happened?
• What did you expect to happen?
• Are there any error messages?

The more details you provide, the better we can help you."
                      maxLength="2000"
                    />
                    {formErrors.description && (
                      <div className="invalid-feedback">{formErrors.description}</div>
                    )}
                    <div className="form-text">
                      Detailed description of your issue (max 2000 characters)
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="d-flex justify-content-end">
                    <button
                      type="submit"
                      className="btn btn-primary px-4"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane me-2"></i>
                          Submit Support Ticket
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support; 